/**
 * `5826` — *create an {item, itemMapping} in {board, targetBoardId}*.
 *
 * 6 production nodes (recipes 123771556, 131523246, 126131757, 75168205, 84791320, …) and the
 * replacement chain of static recipes `111`, `183` and `184`
 * (`discovery/automations/api/static-recipe-mapping.md`).
 *
 * The new item is created on **another** board, so the write is a fan-out: the engine's depth
 * counter is what stops a pair of boards creating items at each other forever (§5.5).
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { applyMappedCells, itemMappingSchema, mappingSourceColumns, resolveItemMapping } from '../item-mapping.js';
import { numberish } from '../values.js';
export const createItemInBoardParams = z.object({
    targetBoardId: numberish.nullable().default(null),
    itemMapping: itemMappingSchema,
    /** Present on the `225`/`223` static shape; kept so a re-import diffs clean. */
    sourceBoardId: numberish.nullable().optional(),
});
export const createItemInBoardAction = defineAction({
    id: '5826',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'create an {item, itemMapping} in {board, targetBoardId}',
    paramsSchema: createItemInBoardParams,
    executable: true,
    references: (params) => [
        ...(params.targetBoardId === null ? [] : [{ kind: 'board', id: String(params.targetBoardId) }]),
        ...mappingSourceColumns(params.itemMapping).map((columnId) => ({ kind: 'column', id: columnId })),
    ],
    async execute(context, params) {
        if (params.targetBoardId === null)
            return { skipped: 'no_target_board' };
        const targetBoardId = String(params.targetBoardId);
        const resolved = await resolveItemMapping(context, params.itemMapping);
        const created = await context.services.createItem({
            boardId: targetBoardId,
            groupId: resolved.groupId,
            name: resolved.name === null || resolved.name.length === 0 ? 'New item' : resolved.name,
        });
        const applied = await applyMappedCells(context, { boardId: targetBoardId, itemId: created.id }, resolved.cells);
        return {
            itemId: created.id,
            boardId: targetBoardId,
            groupId: created.groupId,
            name: created.name,
            written: applied.written,
            failed: applied.failed,
            unresolved: resolved.unresolved,
        };
    },
});
//# sourceMappingURL=5826.js.map